import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getServices, createService, updateService, deleteService } from '../services/api';
import toast from 'react-hot-toast';

export default function ServicesPage() {
  const qc = useQueryClient();
  const { data: services = [], isLoading } = useQuery({ queryKey: ['services'], queryFn: getServices });
  const [form, setForm] = useState({ name_en: '', name_ar: '' });
  const [editing, setEditing] = useState(null);

  const createMut = useMutation({
    mutationFn: createService,
    onSuccess: () => { qc.invalidateQueries(['services']); setForm({ name_en: '', name_ar: '' }); toast.success('Service added'); },
    onError: e => toast.error(e.response?.data?.error || 'Error')
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updateService(id, data),
    onSuccess: () => { qc.invalidateQueries(['services']); setEditing(null); toast.success('Updated'); },
    onError: e => toast.error(e.response?.data?.error || 'Error')
  });
  const deleteMut = useMutation({
    mutationFn: deleteService,
    onSuccess: () => { qc.invalidateQueries(['services']); toast.success('Deleted'); },
    onError: e => toast.error(e.response?.data?.error || 'Error')
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Services</h1>
          <p className="page-subtitle">Manage service categories used in invoices</p>
        </div>
        <div style={{background:'var(--accent)',color:'#fff',borderRadius:10,padding:'8px 18px',fontWeight:700,fontSize:22}}>
          {services.length}
          <span style={{fontSize:11,fontWeight:400,marginLeft:6,opacity:.85}}>Total</span>
        </div>
      </div>

      <div className="card" style={{marginBottom:24,padding:28,borderLeft:'4px solid var(--accent)'}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:18}}>
          <h3 style={{fontSize:15,fontWeight:700,margin:0}}>Add New Service</h3>
        </div>
        <div style={{display:'flex',gap:14,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div style={{flex:1,minWidth:220}}>
            <label className="form-label">Name (English)</label>
            <input className="form-input" value={form.name_en}
              onChange={e=>setForm(p=>({...p,name_en:e.target.value}))}
              placeholder="e.g. Web Development" />
          </div>
          <div style={{flex:1,minWidth:220}}>
            <label className="form-label">الاسم بالعربي</label>
            <input className="form-input" dir="rtl" value={form.name_ar}
              onChange={e=>setForm(p=>({...p,name_ar:e.target.value}))}
              placeholder="مثال: تطوير المواقع" />
          </div>
          <button className="btn btn-primary" style={{minWidth:140,height:42}}
            disabled={!form.name_en||!form.name_ar||createMut.isPending}
            onClick={()=>createMut.mutate(form)}>
            {createMut.isPending ? 'Adding...' : '+ Add Service'}
          </button>
        </div>
      </div>

      <div className="card" style={{overflow:'hidden'}}>
        <div style={{padding:'18px 24px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span style={{fontWeight:700,fontSize:15}}>All Services</span>
          <span style={{fontSize:12,color:'#888'}}>{services.length} service{services.length!==1?'s':''}</span>
        </div>
        {isLoading && <div style={{textAlign:'center',padding:48,color:'#aaa'}}>Loading...</div>}
        {!isLoading && services.length===0 && (
          <div style={{textAlign:'center',padding:60,color:'#aaa'}}>
            <div style={{fontSize:40,marginBottom:12}}>No services yet</div>
            <div style={{fontSize:13}}>Add your first service using the form above</div>
          </div>
        )}
        {!isLoading && services.length>0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{width:50}}>#</th>
                <th>Name (English)</th>
                <th style={{textAlign:'right'}}>الاسم بالعربي</th>
                <th style={{width:180,textAlign:'center'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((svc,idx)=>(
                <tr key={svc.id}>
                  <td>
                    <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:26,height:26,borderRadius:'50%',background:'var(--accent)',color:'#fff',fontSize:11,fontWeight:700}}>
                      {idx+1}
                    </span>
                  </td>
                  <td style={{fontWeight:500}}>
                    {editing?.id===svc.id
                      ? <input className="form-input" style={{padding:'6px 10px',fontSize:13,maxWidth:280}} autoFocus value={editing.name_en} onChange={e=>setEditing(p=>({...p,name_en:e.target.value}))} />
                      : svc.name_en}
                  </td>
                  <td dir="rtl" style={{textAlign:'right'}}>
                    {editing?.id===svc.id
                      ? <input className="form-input" dir="rtl" style={{padding:'6px 10px',fontSize:13,maxWidth:280,textAlign:'right'}} value={editing.name_ar} onChange={e=>setEditing(p=>({...p,name_ar:e.target.value}))} />
                      : <span style={{color:'#444'}}>{svc.name_ar}</span>}
                  </td>
                  <td>
                    {editing?.id===svc.id ? (
                      <div style={{display:'flex',gap:6,justifyContent:'center'}}>
                        <button className="btn btn-primary btn-sm" style={{minWidth:60}}
                          disabled={updateMut.isPending}
                          onClick={()=>updateMut.mutate({id:svc.id,data:{name_en:editing.name_en,name_ar:editing.name_ar}})}>
                          {updateMut.isPending?'...':'Save'}
                        </button>
                        <button className="btn btn-outline btn-sm" onClick={()=>setEditing(null)}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{display:'flex',gap:6,justifyContent:'center'}}>
                        <button className="btn btn-outline btn-sm" style={{minWidth:55}}
                          onClick={()=>setEditing({id:svc.id,name_en:svc.name_en,name_ar:svc.name_ar})}>Edit</button>
                        <button className="btn btn-outline btn-sm" style={{minWidth:65,color:'var(--danger)',borderColor:'var(--danger)'}}
                          onClick={()=>{if(window.confirm('Delete "'+svc.name_en+'"?'))deleteMut.mutate(svc.id);}}>Delete</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
